import { ga, adType, message } from '../config';
import { trEventBe } from '../lib/Analytics';
import AdFactory from '../lib/ad/AdFactory';
import Base from './Base';
import LocalStorage from '../model/LocalStorage';

/**
 * Game
 */
export default class Game extends Base {
    /**
     * constructor
     */
    constructor() {
        super({
            el: '.js-game-play__content',
            gameWrapperEl: '.js-game-play__main-iframe-wrapper',
            gameEl: '.js-game-play__main-iframe',
            adEl: '.js-game-play__ad',
        });

        /** @property {number} adReloadTimeoutId ゲーム中広告のリロードタイマー */
        this.adReloadTimeoutId = null;

        /** @property {class} admodule 広告モジュール */
        this.admodule = null;

        /** @property {LocalStorage} localStorage ローカルストレージclass */
        this.localStorage = new LocalStorage(this.config, 'common');

        /** @property {object} screen ゲーム画面 */
        this.screen = {
            width: 0,
            height: 0,
        };

        /** @property {string} adType ゲーム中広告のタイプ */
        this.gamingAdType = '';

        /** @property {boolean} isDisplayReadyAd ゲーム前広告を表示したか */
        this.isDisplayReadyAd = false;
    }

    /**
     * initialize
     */
    initialize() {
        this.binds();
    }

    /**
     * イベントリスナ登録
     */
    binds() {
        this.$gameEl.addEventListener('load', () => this.observer.emit(message.gameLoad));
    }

    /**
     * ランドスケープ時
     */
    onWindowLandscape() {
        this.$gameWrapperEl.style.display = 'none';
    }

    /**
     * ポートレート時
     *
     * @param {Event} e イベント
     * @param {number} options.effectiveHeight ヘッダを除いたデバイスの高さ
     * @param {function} options.beforeLoadCallback 読み込み前コールバック
     */
    onWindowPortrait(e, options) {
        this.$gameWrapperEl.style.display = '';

        if (options.beforeLoadCallback) {
            options.beforeLoadCallback();
        }

        this.mightLoad(options.effectiveHeight);
    }

    /**
     * ゲームのiframeのloadイベント発火時
     */
    onLoad() {
        if (!this.isDisplayReadyAd) {
            this.loadReloadableAd();
        }
    }

    /**
     * iframeにpostMessageする
     *
     * @param {string} data postMessageのデータ
     */
    postMessage(data) {
        this.$gameEl.contentWindow.postMessage(data, this.config.targetOrigin);
    }

    /**
     * ゲームを読み込む
     *
     * @param {number} effectiveHeight ヘッダを除いたデバイスの高さ
     */
    mightLoad(effectiveHeight) {
        if (this.isReady()) {
            this.loadGame(effectiveHeight);
        }
    }

    /**
     * ゲームの読み込み前か
     */
    isReady() {
        return !this.$gameEl.src;
    }

    /**
     * 広告を入れることができる状態か
     *
     * ## 条件
     * - ゲーム読み込み済
     * - 広告がまだ入っていない
     *
     * @return {boolean}
     */
    canLoadingAd() {
        return !this.isReady() && this.admodule && !this.admodule.isLoaded();
    }

    /**
     * ゲームを読み込む
     *
     * @param {number} effectiveHeight ヘッダを除いたデバイスの高さ
     */
    loadGame(effectiveHeight) {
        // デバイスサイズからゲームの画面サイズを計算
        this.computeSize(effectiveHeight);

        // 挿入する広告のタイプを決める
        this.pickAdType(effectiveHeight);

        // 広告モジュールを作成
        this.createAdModule();

        this.$gameEl.src = this.$gameEl.dataset.src;
        delete this.$gameEl.dataset.src;
    }

    /**
     * 広告を読み込む
     */
    loadReloadableAd() {
        if (!this.admodule) {
            return;
        }

        if (document.visibilityState === 'visible' && this.$gameWrapperEl.style.display !== 'none') {
            this.admodule.load();
        }

        const sec = this.config.adparam.gaming.interval || 0;
        if (sec) {
            this.stopAdReload();
            this.adReloadTimeoutId = setTimeout(() => this.loadReloadableAd(sec), sec);
        }
    }

    /**
     * 広告のリロードを停止する
     */
    stopAdReload() {
        clearTimeout(this.adReloadTimeoutId);
    }

    /**
     * タイトル画面_ゲームスタート押下
     */
    onStartClick() {
        trEventBe(ga.category.click.gameStart, 'スタート', this.commonGALabel);
    }

    /**
     * ゲーム開始
     *
     * @param {Event} e イベント
     * @param {object} payload ペイロード
     */
    onGameStart() {
        trEventBe(ga.category.click.gamePlay, 'ゲーム開始', this.commonGALabel);
    }

    /**
     * ゲーム終了時
     *
     * @param {Event} e イベント
     * @param {object} payload ペイロード
     */
    onGameEnd() {
        trEventBe(ga.category.click.gamePlay, 'ゲーム終了', this.commonGALabel);
        this.stopAdReload();
    }

    /**
     * ゲームリトライ時
     */
    onGameRetry() {
        this.loadReloadableAd();
    }

    /**
     * ゲームのサイズを決定する
     *
     * @param {number} effectiveHeight ゲームに使える高さ
     */
    computeSize(effectiveHeight) {
        // デバイスの高さからゲーム領域以外の要素の高さを引いたものと
        // 渡ってきた高さの比率をピクセルに変換して小さい方を使う
        const { ratio } = this.config;

        const gameHeight = Math.round(this.device.width * ratio);

        const computedHeight = Math.min(effectiveHeight, gameHeight);
        const computedWidth = Math.round(computedHeight / ratio);

        this.$gameWrapperEl.style.paddingTop = `${computedHeight}px`;
        this.$gameWrapperEl.style.width = `${computedWidth}px`;

        this.screen.width = computedWidth;
        this.screen.height = computedHeight;
    }

    /**
     * デバイスの高さをもとにゲーム中広告のタイプを決め広告モジュールを作成する
     *
     * @param {number} effectiveHeight ヘッダーを除いた画面の高さ
     */
    pickAdType(effectiveHeight) {
        const free = effectiveHeight - this.screen.height;
        this.gamingAdType = free > 100 ? adType.gamingLarge : free > 50 ? adType.gamingSmall : '';
    }

    /**
     * デバイスの高さをもとにゲーム中広告のタイプを決め広告モジュールを作成する
     */
    createAdModule() {
        if (!this.gamingAdType) {
            return;
        }

        this.admodule = AdFactory.getInstance(
            this.config.adparam.gaming,
            this.gamingAdType,
            { target: this.$adEl },
        );
    }

    /**
     * 広告のミュート
     */
    muteAd() {
        if (!this.admodule) {
            return;
        }

        this.admodule.hide();
    }

    /**
     * 広告のアンミュート
     */
    unmuteAd() {
        if (!this.admodule) {
            return;
        }

        this.admodule.show();
    }
}
